
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { IoAddOutline } from 'react-icons/io5';
import { FaRegEdit } from "react-icons/fa";
import { MdOutlineDelete } from "react-icons/md";
import { toast } from 'react-toastify';
import axios from 'axios';
import { useUser } from '../context/UserContext';
import { useCustomDialog } from '../context/DialogProvider';
import Stack from "@mui/material/Stack";
import Pagination from "@mui/material/Pagination";
import { format } from 'date-fns';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import api from '../utils/api';



// Helper to get status label and color
function getStatusProps(status) {
  const normalized = (status || "").toString().trim().toUpperCase();
  switch (normalized) {
    case "APPROVED":
      return { label: "Approved", color: "bg-green-100 text-green-800" };
    case "PENDING":
      return { label: "Pending", color: "bg-yellow-100 text-yellow-800" };
    case "REJECTED":
      return { label: "Rejected", color: "bg-red-100 text-red-800" };
      case "CANCELLED":
      return { label: "Rejected", color: "bg-red-100 text-red-800" };
    default:
      return { label: "Unknown", color: "bg-gray-100 text-gray-800" };
  }
}



const TIME_CONFIG = {
  FULL_DAY: { label: 'Full Day', duration: 1 },
  FIRST_HALF: { label: 'First Half', duration: 0.5 },
  SECOND_HALF: { label: 'Second Half', duration: 0.5 }
};



function OnDuty() {

  const baseUrl = process.env.REACT_APP_API_URL;
  const { currentUser,loading } = useUser();
  const{confirm} = useCustomDialog();
  const [onDuties, setOnDuties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [newOnDuty, setNewOnDuty] = useState({
    onDate: '',
    location: '',
    duration: TIME_CONFIG.FULL_DAY.duration,
    reason: '',
    timing: 'FULL_DAY'
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewOnDuty, setViewOnDuty] = useState(null);
  const onDutiesPerPage = 6;
  const [holidays,setHolidays]=useState([])

  // Pagination logic
  const totalPages = Math.ceil(onDuties.length / onDutiesPerPage);
  const paginatedOnDuties = onDuties.slice(
    (currentPage - 1) * onDutiesPerPage,
    currentPage * onDutiesPerPage
  );

  const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = (`0${date.getMonth() + 1}`).slice(-2); // months are 0-indexed
  const day = (`0${date.getDate()}`).slice(-2);
  return `${year}-${month}-${day}`;
};

  const isWeekdayAndNotHoliday = (date, holidays = []) => {
  if (!date || !Array.isArray(holidays)) return true;

  const day = date.getDay(); // 0 = Sunday, 6 = Saturday
  const isWeekend = day === 0 || day === 6;

  const formatted = formatLocalDate(date); // 👈 Use local time

  const isHoliday = holidays.some(holiday => holiday.holidayDate === formatted);

  return !isWeekend && !isHoliday;
};


  const fetchHolidays = async () => {
    
    try {
      const response = await api.get(`/api/holidays`);
      setHolidays(response.data);
      console.log(response.data)
    } catch (error) {
      console.error("Error fetching holidays:", error);
    }
};

useEffect(() => {
    fetchHolidays();
}, []);

  // Add fetch function
  const fetchOnDuties = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/api/onduty/${currentUser?.id}`);
      setOnDuties(response.data);
    } catch (error) {
      console.error('Error fetching on duty:', error);
      toast.error('Failed to fetch on duty requests');
    } finally {
      setIsLoading(false);
    }
  };



  // Add useEffect to fetch data on component mount
  useEffect(() => {
    if(!loading){
      fetchOnDuties();
    }
  }, [currentUser?.id]);

  const handleDelete = async (id) => {
    const confirmed = await confirm("Are you sure you want to delete this On Duty request?");
    try {
      if (confirmed) {
        await api.delete(`/api/onduty/${id}`, {
          params: { employeeId: currentUser?.id }
        });
      }
      fetchOnDuties();
      toast.success('On Duty request deleted successfully');
    } catch (error) {
      console.error('Error deleting On Duty:', error);
      toast.error('Failed to delete On Duty request');
    }
  };



  const handleEdit = (onDuty) => {
    setNewOnDuty({
      id: onDuty.id,
      onDate: onDuty.onDate,
      location: onDuty.location || '',
      duration: onDuty.duration || TIME_CONFIG[onDuty.timing]?.duration || TIME_CONFIG.FULL_DAY.duration,
      reason: onDuty.reason,
      timing: onDuty.timing || 'FULL_DAY'
    });
    setIsEditing(true);
    setShowApplyForm(true);
  };



  const handleView = (onDuty) => {
    setViewOnDuty(onDuty);
    setShowViewModal(true);
  };



  const formatDateForBackend = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };



  const payload = {
    ...newOnDuty,
    onDate: formatDateForBackend(newOnDuty.onDate),
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (isEditing) {
        await api.put(`/api/onduty/${newOnDuty.id}?employeeId=${currentUser?.id}`, payload);
        toast.success('On Duty request updated successfully');
      } else {
        await api.post(`/api/onduty/apply?employeeId=${currentUser?.id}`, payload);
        toast.success('On Duty request submitted successfully');
      }

      fetchOnDuties();
      setShowApplyForm(false);
      setIsEditing(false);
      setNewOnDuty({ onDate: '', location: '', duration: TIME_CONFIG.FULL_DAY.duration, reason: '', timing: 'FULL_DAY' });
    } catch (error) {
      toast.error(isEditing ? 'Failed to update On Duty request' : 'Failed to submit On Duty request');
    } finally {
      setIsSubmitting(false);
    }
  };



  // Keep the main structure and only show loading in table rows
  return (
    <div className="sm:p-6 sm:mt-auto mt-8 am:mb-auto mb-20 w-full mx-auto">
      <motion.div 
        className="flex justify-between items-center mb-6 bg-slate-200 rounded-2xl p-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
      <h2 className="text-xl xs:text-2xl font-bold">My On Duty Requests</h2>
        <motion.button
          onClick={() => setShowApplyForm(true)}
          className="bg-slate-800 text-white px-4 py-2 rounded-full hover:bg-slate-700 flex items-center gap-2"
        >
          <IoAddOutline className="text-lg" />
          <span>Apply On duty</span>
        </motion.button>
      </motion.div>

      {/* Desktop View */}
      <motion.div
        className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white">
              <tr>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">SL No</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">Date</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">Session</th>
                 <th className="px-6 py-4 font-semibold text-slate-600 text-left">Location</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-left">Status</th>
                <th className="px-6 py-4 font-semibold text-slate-600 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <svg
                        className="animate-spin h-8 w-8 text-slate-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8z"
                        ></path>
                      </svg>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedOnDuties.length > 0 ? (
                  paginatedOnDuties.map((onDuty, index) => (
                    <tr key={onDuty.id} className="hover:bg-slate-50/50 transition-colors duration-150">
                      <td className="px-6 py-4 text-slate-600">
                        {(currentPage - 1) * onDutiesPerPage + index + 1}
                      </td>
                      <td className="px-6 py-4 text-slate-600">{
  format(
    new Date(
      new Date(onDuty.onDate).toLocaleString("en-US", {
        timeZone: "Asia/Kolkata",
      })
    ),
    "dd-MM-yyyy"
  )
}</td>
                      <td className="px-6 py-4 text-slate-600">{onDuty.timing==="FULL_DAY" ? "Full day":onDuty.timing==="FIRST_HALF"? "First half":"Second half"}</td>
                      <td className="px-6 py-4 text-slate-600">{onDuty.location}</td>
                      <td className="px-6 py-4">
                        {(() => {
                          const { label, color } = getStatusProps(onDuty.status ?? onDuty.onDutyStatus);
                          return (
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${color}`}>
                              {label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-center gap-3">
                          {(() => {
                            const status = (onDuty.status ?? onDuty.onDutyStatus ?? "").toString().trim().toUpperCase();
                            if (status === "PENDING") {
                              return <>
                                <motion.button
                                  onClick={() => handleEdit(onDuty)}
                                  className="text-yellow-600 hover:text-yellow-800 transition-colors"
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  title="Edit"
                                >
                                  <FaRegEdit className="text-xl" />
                                </motion.button>
                                <motion.button
                                  onClick={() => handleDelete(onDuty.id)}
                                  className="text-red-600 hover:text-red-800 transition-colors"
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  title="Delete"
                                >
                                  <MdOutlineDelete className="text-xl" />
                                </motion.button>
                              </>;
                            } else {
                              return <motion.button
                                onClick={() => handleView(onDuty)}
                                className="text-blue-600 hover:text-blue-800 transition-colors"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                title="View"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </motion.button>;
                            }
                          })()}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-slate-400">
                      No On Duty requests found
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-slate-100">
          <Stack spacing={2} className="flex justify-end">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              variant="outlined"
              shape="rounded-full"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#475569",
                  borderColor: "#e2e8f0",
                },
                "& .Mui-selected": {
                  backgroundColor: "#475569 !important",
                  color: "#fff",
                },
              }}
            />
          </Stack>
        </div>
      </motion.div>

      {/* Mobile View */}
      <div className="sm:hidden space-y-4">
        {isLoading ? (
          <div className="bg-white p-5 rounded-xl shadow-md border border-slate-100">
            <div className="flex justify-center items-center py-8">
              <svg
                className="animate-spin h-8 w-8 text-slate-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                ></path>
              </svg>
            </div>
          </div>
        ) : (
          paginatedOnDuties.length > 0 ? (
            paginatedOnDuties.map((onDuty, index) => (
              <div
                key={onDuty.id}
                className="bg-white p-5 rounded-xl shadow-md border border-slate-100"
              >
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <h3 className="font-semibold text-slate-800">Request #{index + 1}</h3>
                    {(() => {
                      const { label, color } = getStatusProps(onDuty.onDutyStatus);
                      return (
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${color}`}>
                          {label}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">On Duty Date:</span>
                      <span className="text-slate-700">{
  format(
    new Date(
      new Date(onDuty.onDate).toLocaleString("en-US", {
        timeZone: "Asia/Kolkata",
      })
    ),
    "dd-MM-yyyy"
  )
}</span>
                    </div>
                    <div className="flex flex-col gap-1 text-sm">
                      <span className="text-slate-500">Reason:</span>
                      <span className="text-slate-700">{onDuty.reason}</span>
                    </div>
                  </div>
                </div>
              </div>
            )) 
          ) : (
            <div className="bg-white p-8 rounded-xl text-center text-slate-400">
              No On Duty data found
            </div>
          )
        )}
        <div className="mt-6 flex justify-center">
          <Stack spacing={2}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              variant="outlined"
              shape="rounded-full"
              size="medium"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#475569",
                  borderColor: "#e2e8f0",
                },
                "& .Mui-selected": {
                  backgroundColor: "#475569 !important",
                  color: "#fff",
                },
              }}
            />
          </Stack>
        </div>
      </div>


      {/* View Modal */}
      {showViewModal && viewOnDuty && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowViewModal(false)}
        >
          <motion.div 
            className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800">On Duty Details</h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            {/* View Modal Content */}
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-500">On Duty Date</p>
                <div className="flex justify-between items-center">
                  <p className="font-medium text-slate-800">{
  format(
    new Date(
      new Date(viewOnDuty.onDate).toLocaleString("en-US", {
        timeZone: "Asia/Kolkata",
      })
    ),
    "dd-MM-yyyy"
  )
}</p>
                  {(() => {
                    const { label, color } = getStatusProps(viewOnDuty.status ?? viewOnDuty.onDutyStatus);
                    return (
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${color}`}>
                        {label}
                      </span>
                    );
                  })()}
                </div>
              </div>
              <div>
                <p className="text-sm text-slate-500">Reason</p>
                <p className="font-medium text-slate-800">{viewOnDuty.reason}</p>
              </div>
              {viewOnDuty.onDutyStatus === "REJECTED" && viewOnDuty.approverComment && (
                <div>
                  <p className="text-sm text-slate-500">Rejection Reason</p>
                  <p className="font-medium text-slate-800">{viewOnDuty.approverComment}</p>
                </div>
              )}
              {/* Add close button at bottom */}
              <div className="flex justify-center pt-4">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="w-full px-4 py-2 text-sm font-medium text-slate-50 hover:text-slate-800 rounded-full bg-slate-600 hover:bg-slate-400"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Apply Form Modal */}
      {showApplyForm && (
        <div 
          className="fixed inset-0 bg-black backdrop-blur-sm bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (!isSubmitting && e.target === e.currentTarget) {
              setShowApplyForm(false);
              setIsEditing(false);
               setNewOnDuty({ onDate: '', reason: '',duration: TIME_CONFIG.FULL_DAY.duration,timing: 'FULL_DAY' });
            }
          }}
        >
          <motion.div 
            className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800">
                {isEditing ? 'Edit On Duty Application' : 'On Duty Application'}
              </h3>
              <button
                onClick={() => {
                  if (!isSubmitting) {
                    setShowApplyForm(false);
                    setIsEditing(false);
                     setNewOnDuty({ onDate: '', reason: '',duration: TIME_CONFIG.FULL_DAY.duration,timing: 'FULL_DAY' });
                  }
                }}
                disabled={isSubmitting}
                className={`text-slate-400 hover:text-slate-600 ${
                  isSubmitting ? 'cursor-not-allowed opacity-50' : ''
                }`}
              >
                ✕
              </button>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              
                <div className="grid grid-cols-2 gap-4">

              <div  >
                <label className="block text-sm font-medium text-slate-700 mb-1">On Duty Date</label>
               <DatePicker
  selected={newOnDuty.onDate}
  onChange={(date) => setNewOnDuty({ ...newOnDuty, onDate: date })}
  dateFormat="dd-MM-yyyy"
  placeholderText="📅 Select date"
  filterDate={(date) => isWeekdayAndNotHoliday(date, holidays)}
  required
   disabled={isEditing}
 className="w-full border border-slate-200 rounded-lg p-2.5 pl-10 focus:outline-none focus:border-slate-500"
/>


              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Session</label>
                <select
                  value={newOnDuty.timing}
                  onChange={(e) => {
                    const selectedKey = e.target.value;
                    const config = TIME_CONFIG[selectedKey];
                    setNewOnDuty({
                      ...newOnDuty,
                      duration: config.duration,
                      timing: selectedKey
                    });
                  }}
                  className="w-full border border-slate-200 rounded-lg p-3 pl-10 focus:outline-none focus:border-slate-500"
                  required
                >
                  {Object.entries(TIME_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>

             </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
                <input
                  type="text"
                  value={newOnDuty.location}
                  onChange={(e) => setNewOnDuty({...newOnDuty, location: e.target.value})}
                  className="w-full border border-slate-200 rounded-lg p-2.5"
                  required
                />
              </div>

             

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reason</label>
                <textarea
                  value={newOnDuty.reason}
                  onChange={(e) => setNewOnDuty({...newOnDuty, reason: e.target.value})}
                  className="w-full border border-slate-200 rounded-lg p-2.5"
                  rows="3"
                  required
                ></textarea>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowApplyForm(false);
                    setIsEditing(false);
                    setNewOnDuty({ onDate: '', reason: '',duration: TIME_CONFIG.FULL_DAY.duration,timing: 'FULL_DAY' });
                  }}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-4 py-2 text-sm font-medium bg-slate-800 text-white rounded-full hover:bg-slate-700 
                              disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]`}
                  >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>{isEditing ? 'Updating...' : 'Submitting...'}</span>
                    </div>
                  ) : (
                    <span>{isEditing ? 'Update' : 'Submit'}</span>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default OnDuty;